import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { getInvoicesForMetrics } from "../WriteToDb";

const formatCurrency = (value) => `£${Number(value || 0).toFixed(2)}`;

const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "N/A";
  }
  return `${Number(value).toFixed(decimals)}%`;
};

const formatNumber = (value, decimals = 1, suffix = "") => {
  const n = Number(value);
  if (!Number.isFinite(n)) return `0${suffix}`;
  return `${n.toFixed(decimals)}${suffix}`;
};

const LAPSED_MULTIPLIER = 1.5;
const LARGE_JOB_THRESHOLD = 500;
const MetricsPage = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);

  const [patternModalMode, setPatternModalMode] = useState(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await getInvoicesForMetrics();
      setInvoices(rows ?? []);
    } catch (e) {
      console.warn("Failed to load metrics", e);
      setError(e?.message || "Failed to load metrics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const data = useMemo(() => {
    if (!invoices || invoices.length === 0) return null;

    const totalRevenue = invoices.reduce((s, i) => s + (i.total || 0), 0);
    const paidInvoices = invoices.filter((i) => i.isPaid);
    const unpaidInvoices = invoices.filter((i) => !i.isPaid);

    const paidRevenue = paidInvoices.reduce((s, i) => s + (i.total || 0), 0);
    const unpaidRevenue = unpaidInvoices.reduce(
      (s, i) => s + (i.total || 0),
      0
    );

    const totalInvoicesCount = invoices.length;

    const invoiceTotals = invoices.map((i) => i.total || 0);
    const avgInvoice =
      totalInvoicesCount > 0
        ? invoiceTotals.reduce((s, v) => s + v, 0) / totalInvoicesCount
        : 0;

    const sortedTotals = [...invoiceTotals].sort((a, b) => a - b);
    let medianInvoice = 0;
    if (sortedTotals.length) {
      const mid = Math.floor(sortedTotals.length / 2);
      medianInvoice =
        sortedTotals.length % 2 === 0
          ? (sortedTotals[mid - 1] + sortedTotals[mid]) / 2
          : sortedTotals[mid];
    }

    const paidCount = paidInvoices.length;
    const unpaidCount = unpaidInvoices.length;

    const paidCountPct =
      totalInvoicesCount > 0 ? (paidCount / totalInvoicesCount) * 100 : 0;
    const paidRevenuePct =
      totalRevenue > 0 ? (paidRevenue / totalRevenue) * 100 : 0;

    const paidWithDates = paidInvoices.filter(
      (i) => i.paidAt && (i.invoiceDate || i.createdAt)
    );

    const daysToPayArray = paidWithDates
      .map((i) => {
        const start = i.invoiceDate ? dayjs(i.invoiceDate) : dayjs(i.createdAt);
        const end = dayjs(i.paidAt);
        return end.diff(start, "day");
      })
      .filter((d) => d >= 0);

    const avgDaysToPay =
      daysToPayArray.length > 0
        ? daysToPayArray.reduce((s, v) => s + v, 0) / daysToPayArray.length
        : 0;

    let medianDaysToPay = 0;
    if (daysToPayArray.length) {
      const sorted = [...daysToPayArray].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      medianDaysToPay =
        sorted.length % 2 === 0
          ? (sorted[mid - 1] + sorted[mid]) / 2
          : sorted[mid];
    }

    const revenueByMonth = {};
    const invoicesByMonthCount = {};

    invoices.forEach((inv) => {
      const dateKey = inv.invoiceDate
        ? dayjs(inv.invoiceDate).format("YYYY-MM")
        : dayjs(inv.createdAt).format("YYYY-MM");

      revenueByMonth[dateKey] =
        (revenueByMonth[dateKey] || 0) + (inv.total || 0);
      invoicesByMonthCount[dateKey] = (invoicesByMonthCount[dateKey] || 0) + 1;
    });

    const monthLabels = Object.keys(revenueByMonth).sort();
    let bestMonthLabel = null;
    let bestMonthRevenue = 0;
    monthLabels.forEach((label) => {
      const value = revenueByMonth[label];
      if (value > bestMonthRevenue) {
        bestMonthRevenue = value;
        bestMonthLabel = label;
      }
    });

    const today = dayjs();
    const currentMonthKey = today.format("YYYY-MM");
    const lastMonthKey = today.subtract(1, "month").format("YYYY-MM");

    const revenueThisMonth = revenueByMonth[currentMonthKey] || 0;
    const revenueLastMonth = revenueByMonth[lastMonthKey] || 0;

    const invoicesThisMonth = invoicesByMonthCount[currentMonthKey] || 0;
    const invoicesLastMonth = invoicesByMonthCount[lastMonthKey] || 0;

    const revenueChangePct =
      revenueLastMonth > 0
        ? ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100
        : null;

    const invoiceChangePct =
      invoicesLastMonth > 0
        ? ((invoicesThisMonth - invoicesLastMonth) / invoicesLastMonth) * 100
        : null;

    const agingBuckets = {
      "0-30": { count: 0, amount: 0 },
      "31-60": { count: 0, amount: 0 },
      "61-90": { count: 0, amount: 0 },
      "90+": { count: 0, amount: 0 },
    };

    unpaidInvoices.forEach((inv) => {
      const baseDate = inv.invoiceDate
        ? dayjs(inv.invoiceDate)
        : dayjs(inv.createdAt);
      const days = today.diff(baseDate, "day");
      let key;

      if (days <= 30) key = "0-30";
      else if (days <= 60) key = "31-60";
      else if (days <= 90) key = "61-90";
      else key = "90+";

      agingBuckets[key].count += 1;
      agingBuckets[key].amount += inv.total || 0;
    });

    const outstandingOver60 =
      agingBuckets["61-90"].amount + agingBuckets["90+"].amount;

    const totalDiscount = invoices.reduce((s, i) => s + (i.discount || 0), 0);
    const invoicesWithDiscount = invoices.filter(
      (i) => (i.discount || 0) > 0
    ).length;
    const discountInvoicePct =
      totalInvoicesCount > 0
        ? (invoicesWithDiscount / totalInvoicesCount) * 100
        : 0;
    const avgDiscountPerInvoice =
      totalInvoicesCount > 0 ? totalDiscount / totalInvoicesCount : 0;

    const totalDeposit = invoices.reduce((s, i) => s + (i.deposit || 0), 0);
    const avgDepositPercent =
      totalRevenue > 0 ? (totalDeposit / totalRevenue) * 100 : 0;

    const customerTotals = {};
    const invoicesByCustomer = {};

    invoices.forEach((inv) => {
      const name = inv.customerName || "Unknown";
      if (!customerTotals[name]) customerTotals[name] = 0;
      customerTotals[name] += inv.total || 0;

      if (!invoicesByCustomer[name]) invoicesByCustomer[name] = [];
      invoicesByCustomer[name].push(inv);
    });

    const topCustomers = Object.entries(customerTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    const customerNames = Object.keys(invoicesByCustomer);
    const activeCustomersCount = customerNames.length;

    let repeatCustomersCount = 0;
    let singleInvoiceCustomersCount = 0;
    customerNames.forEach((name) => {
      const count = invoicesByCustomer[name].length;
      if (count > 1) repeatCustomersCount += 1;
      else singleInvoiceCustomersCount += 1;
    });

    const avgInvoicesPerCustomer =
      activeCustomersCount > 0 ? totalInvoicesCount / activeCustomersCount : 0;

    const sortedCustomerByRevenue = Object.entries(customerTotals).sort(
      (a, b) => b[1] - a[1]
    );
    const top3Revenue = sortedCustomerByRevenue
      .slice(0, 3)
      .reduce((s, [, amount]) => s + amount, 0);
    const top3SharePct =
      totalRevenue > 0 ? (top3Revenue / totalRevenue) * 100 : 0;

    const thirtyDaysAgo = today.subtract(30, "day");
    let invoicesLast30Days = 0;
    let revenueLast30Days = 0;

    invoices.forEach((inv) => {
      const baseDate = inv.invoiceDate
        ? dayjs(inv.invoiceDate)
        : dayjs(inv.createdAt);
      if (
        baseDate.isAfter(thirtyDaysAgo) ||
        baseDate.isSame(thirtyDaysAgo, "day")
      ) {
        invoicesLast30Days += 1;
        revenueLast30Days += inv.total || 0;
      }
    });

    const avgRevenuePerDayLast30 = revenueLast30Days / 30;

    const serviceHistory = {};

    invoices.forEach((inv) => {
      const baseDate = inv.invoiceDate
        ? dayjs(inv.invoiceDate)
        : dayjs(inv.createdAt);
      const customerName = inv.customerName || "Unknown";
      const items = Array.isArray(inv.items) ? inv.items : [];

      const invoiceJobName =
        inv.jobName ||
        inv.job_name ||
        (inv.job && (inv.job.name || inv.job.job_name)) ||
        null;

      if (!items.length) return;

      items.forEach((item) => {
        const rawDesc = item.description || "Service";

        let jobName = null;
        if (item.jobName || item.job_name) {
          jobName = String(item.jobName ?? item.job_name);
        } else if (invoiceJobName) {
          jobName = String(invoiceJobName);
        } else {
          const desc = String(rawDesc);
          let idx = desc.indexOf(" – ");
          if (idx === -1) idx = desc.indexOf(" - ");
          if (idx > 0) {
            jobName = desc.slice(0, idx).trim();
          } else {
            jobName = desc.trim();
          }
        }

        const serviceName = rawDesc;
        const key = `${customerName}::${serviceName}`;

        if (!serviceHistory[key]) {
          serviceHistory[key] = {
            customerName,
            serviceName,
            entries: [],
          };
        }

        const qty =
          typeof item.qty === "number" ? item.qty : Number(item.qty) || 0;

        const unitPrice =
          typeof item.unitPrice === "number"
            ? item.unitPrice
            : typeof item.unit_price === "number"
            ? item.unit_price
            : Number(item.unitPrice ?? item.unit_price) || 0;

        const lineAmount = qty * unitPrice || 0;

        serviceHistory[key].entries.push({
          date: baseDate,
          amount: lineAmount > 0 ? lineAmount : inv.total || 0,
          jobName,
        });
      });
    });

    const lapsedServices = [];
    const activeServices = [];
    const oneTimeLargeServices = [];

    Object.values(serviceHistory).forEach((entry) => {
      const { customerName, serviceName, entries } = entry;
      if (!entries.length) return;

      const sortedEntries = [...entries].sort(
        (a, b) => a.date.valueOf() - b.date.valueOf()
      );
      const lastEntry = sortedEntries[sortedEntries.length - 1];
      const occurrences = sortedEntries.length;
      const totalAmount = sortedEntries.reduce(
        (s, e) => s + (e.amount || 0),
        0
      );

      const history = sortedEntries.map((e) => ({
        dateLabel: e.date.format("DD MMM YYYY"),
        amount: e.amount,
        jobName: e.jobName ?? null,
      }));

      if (occurrences >= 2) {
        const intervals = [];
        for (let i = 1; i < sortedEntries.length; i++) {
          intervals.push(
            sortedEntries[i].date.diff(sortedEntries[i - 1].date, "day")
          );
        }
        if (!intervals.length) return;

        const sortedIntervals = [...intervals].sort((a, b) => a - b);
        const mid = Math.floor(sortedIntervals.length / 2);
        const medianInterval =
          sortedIntervals.length % 2 === 0
            ? (sortedIntervals[mid - 1] + sortedIntervals[mid]) / 2
            : sortedIntervals[mid];

        const daysSinceLast = today.diff(lastEntry.date, "day");

        const alert = {
          customerName,
          serviceName,
          lastDateLabel: lastEntry.date.format("DD MMM YYYY"),
          medianIntervalDays: medianInterval,
          daysSinceLast,
          totalAmount,
          occurrences,
          history,
        };

        if (daysSinceLast > medianInterval * LAPSED_MULTIPLIER) {
          lapsedServices.push(alert);
        } else {
          activeServices.push(alert);
        }
      } else {
        if (totalAmount >= LARGE_JOB_THRESHOLD) {
          oneTimeLargeServices.push({
            customerName,
            serviceName,
            lastDateLabel: lastEntry.date.format("DD MMM YYYY"),
            totalAmount,
            occurrences,
            history,
          });
        }
      }
    });

    lapsedServices.sort(
      (a, b) => (b.daysSinceLast || 0) - (a.daysSinceLast || 0)
    );
    activeServices.sort((a, b) => b.totalAmount - a.totalAmount);
    oneTimeLargeServices.sort((a, b) => b.totalAmount - a.totalAmount);

    return {
      totalRevenue,
      paidRevenue,
      unpaidRevenue,
      totalInvoicesCount,
      avgInvoice,
      medianInvoice,
      paidCount,
      unpaidCount,
      paidCountPct,
      paidRevenuePct,
      activeCustomersCount,
      revenueThisMonth,
      revenueLastMonth,
      invoicesThisMonth,
      invoicesLastMonth,
      revenueChangePct,
      invoiceChangePct,
      avgDaysToPay,
      medianDaysToPay,
      agingBuckets,
      outstandingOver60,
      totalDiscount,
      invoicesWithDiscount,
      discountInvoicePct,
      avgDiscountPerInvoice,
      totalDeposit,
      avgDepositPercent,
      invoicesLast30Days,
      revenueLast30Days,
      avgRevenuePerDayLast30,
      bestMonthLabel,
      bestMonthRevenue,
      topCustomers,
      repeatCustomersCount,
      singleInvoiceCustomersCount,
      avgInvoicesPerCustomer,
      top3SharePct,
      lapsedServices,
      activeServices,
      oneTimeLargeServices,
    };
  }, [invoices]);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm text-sm text-slate-600">
          Loading metrics…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
        No invoices yet. Create some invoices to see analytics.
      </div>
    );
  }

  const {
    totalRevenue,
    paidRevenue,
    unpaidRevenue,
    totalInvoicesCount,
    avgInvoice,
    medianInvoice,
    paidCount,
    unpaidCount,
    paidCountPct,
    paidRevenuePct,
    activeCustomersCount,
    revenueThisMonth,
    revenueLastMonth,
    invoicesThisMonth,
    invoicesLastMonth,
    revenueChangePct,
    invoiceChangePct,
    avgDaysToPay,
    medianDaysToPay,
    agingBuckets,
    outstandingOver60,
    totalDiscount,
    invoicesWithDiscount,
    discountInvoicePct,
    avgDiscountPerInvoice,
    totalDeposit,
    avgDepositPercent,
    invoicesLast30Days,
    revenueLast30Days,
    avgRevenuePerDayLast30,
    bestMonthLabel,
    bestMonthRevenue,
    topCustomers,
    repeatCustomersCount,
    singleInvoiceCustomersCount,
    avgInvoicesPerCustomer,
    top3SharePct,
    lapsedServices,
    activeServices,
    oneTimeLargeServices,
  } = data;

  const patternItems =
    patternModalMode === "lapsed"
      ? lapsedServices
      : patternModalMode === "active"
      ? activeServices
      : oneTimeLargeServices;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Metrics</h1>
        <p className="text-sm text-slate-500">
          Deep dive into how your invoicing business is performing.
        </p>
      </div>

      <section className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        <KpiCard label="Total revenue" value={formatCurrency(totalRevenue)} />
        <KpiCard label="Paid revenue" value={formatCurrency(paidRevenue)} />
        <KpiCard label="Outstanding" value={formatCurrency(unpaidRevenue)} />
        <KpiCard
          label="Total invoices"
          value={String(totalInvoicesCount)}
          helper={`${paidCount} paid · ${unpaidCount} unpaid`}
        />
        <KpiCard
          label="Avg invoice"
          value={formatCurrency(avgInvoice)}
          helper={`Median ${formatCurrency(medianInvoice)}`}
        />
        <KpiCard
          label="Active customers"
          value={String(activeCustomersCount)}
        />
      </section>

      <section className="grid gap-3 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Revenue this month
          </h2>
          <p className="mt-1 text-xs text-slate-500">Compared to last month</p>
          <div className="mt-3 flex items-end justify-between">
            <div>
              <div className="text-xl font-semibold text-slate-900">
                {formatCurrency(revenueThisMonth)}
              </div>
              <div className="text-xs text-slate-500">
                Last month: {formatCurrency(revenueLastMonth)}
              </div>
            </div>
            <ChangePill value={revenueChangePct} />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Invoices this month
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Count of invoices issued
          </p>
          <div className="mt-3 flex items-end justify-between">
            <div>
              <div className="text-xl font-semibold text-slate-900">
                {invoicesThisMonth}
              </div>
              <div className="text-xs text-slate-500">
                Last month: {invoicesLastMonth}
              </div>
            </div>
            <ChangePill value={invoiceChangePct} />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">Last 30 days</h2>
        <p className="mt-1 text-xs text-slate-500">Recent activity snapshot.</p>
        <div className="mt-3 space-y-1.5">
          <InfoRow label="Invoices" value={String(invoicesLast30Days)} />
          <InfoRow label="Revenue" value={formatCurrency(revenueLast30Days)} />
          <InfoRow
            label="Avg per day"
            value={formatCurrency(avgRevenuePerDayLast30)}
          />
          {bestMonthLabel && (
            <InfoRow
              label="Best month so far"
              value={`${dayjs(`${bestMonthLabel}-01`).format(
                "MMM YYYY"
              )} (${formatCurrency(bestMonthRevenue)})`}
            />
          )}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">
          Discounts &amp; deposits
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          How much you give away and how much you take upfront.
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <InfoRow
              label="Total discounts"
              value={formatCurrency(totalDiscount)}
            />
            <InfoRow
              label="Avg discount / invoice"
              value={formatCurrency(avgDiscountPerInvoice)}
            />
            <InfoRow
              label="Invoices with discount"
              value={`${invoicesWithDiscount} (${formatPercent(
                discountInvoicePct,
                1
              )})`}
            />
          </div>
          <div className="space-y-1.5">
            <InfoRow
              label="Total deposits"
              value={formatCurrency(totalDeposit)}
            />
            <InfoRow
              label="Avg deposit % of revenue"
              value={formatPercent(avgDepositPercent, 1)}
            />
          </div>
        </div>
      </section>

      <section className="grid gap-3 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Payment health
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            How quickly money comes in.
          </p>
          <div className="mt-3 space-y-1.5">
            <InfoRow
              label="Avg days to pay"
              value={formatNumber(avgDaysToPay, 1, "d")}
            />
            <InfoRow
              label="Median days to pay"
              value={formatNumber(medianDaysToPay, 1, "d")}
            />
            <InfoRow
              label="Paid invoices"
              value={`${paidCount} / ${totalInvoicesCount} (${formatPercent(
                paidCountPct,
                1
              )})`}
            />
            <InfoRow
              label="Paid (by revenue)"
              value={formatPercent(paidRevenuePct, 1)}
            />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Paid vs outstanding
          </h2>
          <p className="mt-1 text-xs text-slate-500">Split by revenue.</p>
          <div className="mt-3">
            <PaidVsOutstandingBar
              paidRevenue={paidRevenue}
              unpaidRevenue={unpaidRevenue}
              totalRevenue={totalRevenue}
            />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">
          Aging of unpaid invoices
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          How long overdue your outstanding invoices are.
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-4">
          {["0-30", "31-60", "61-90", "90+"].map((bucket) => {
            const bucketData = agingBuckets[bucket] || {
              amount: 0,
              count: 0,
            };
            return (
              <div
                key={bucket}
                className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
              >
                <div className="text-[11px] font-medium text-slate-500">
                  {bucket} days
                </div>
                <div className="text-sm font-semibold text-slate-900">
                  {formatCurrency(bucketData.amount)}
                </div>
                <div className="text-[11px] text-slate-500">
                  {bucketData.count} invoice
                  {bucketData.count === 1 ? "" : "s"}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-xs text-slate-500">
          Outstanding &gt; 60 days:{" "}
          <span className="font-semibold text-slate-900">
            {formatCurrency(outstandingOver60)}
          </span>
        </div>
      </section>

      <section className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">Customer mix</h2>
          <p className="mt-1 text-xs text-slate-500">
            How your work is distributed.
          </p>
          <div className="mt-3 space-y-1.5">
            <InfoRow
              label="Active customers"
              value={String(activeCustomersCount)}
            />
            <InfoRow
              label="Repeat customers"
              value={String(repeatCustomersCount)}
            />
            <InfoRow
              label="One-off customers"
              value={String(singleInvoiceCustomersCount)}
            />
            <InfoRow
              label="Avg invoices per customer"
              value={formatNumber(avgInvoicesPerCustomer, 1)}
            />
            <InfoRow
              label="Top 3 revenue share"
              value={formatPercent(top3SharePct, 1)}
            />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Top customers
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Biggest revenue drivers.
          </p>
          <div className="mt-3 divide-y divide-slate-100">
            {topCustomers.length === 0 ? (
              <p className="py-2 text-sm text-slate-500">No customers yet.</p>
            ) : (
              topCustomers.map(([name, amount], idx) => (
                <div
                  key={`${name}-${idx}`}
                  className="flex items-center justify-between py-2"
                >
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs text-slate-400">#{idx + 1}</span>
                    <span className="text-sm text-slate-800">
                      {name || "Unknown"}
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-slate-900">
                    {formatCurrency(amount)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">
          Service patterns
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Recurring services, likely churn, and big one-off jobs.
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <PatternCard
            label="Not repeating"
            subtitle="Likely churn"
            count={lapsedServices.length}
            onClick={() => setPatternModalMode("lapsed")}
          />
          <PatternCard
            label="Repeating"
            subtitle="Active regulars"
            count={activeServices.length}
            onClick={() => setPatternModalMode("active")}
          />
          <PatternCard
            label="One-time large"
            subtitle="Big single jobs"
            count={oneTimeLargeServices.length}
            onClick={() => setPatternModalMode("one-time")}
          />
        </div>
      </section>

      {patternModalMode && (
        <ServicePatternModal
          mode={patternModalMode}
          items={patternItems}
          onClose={() => setPatternModalMode(null)}
        />
      )}
    </div>
  );
};

const KpiCard = ({ label, value, helper }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
    {helper && <div className="mt-1 text-xs text-slate-500">{helper}</div>}
  </div>
);

const ChangePill = ({ value }) => {
  if (value === null || value === undefined) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
        No previous data
      </span>
    );
  }
  const positive = value >= 0;
  return (
    <span
      className={[
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        positive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700",
      ].join(" ")}
    >
      {positive ? "▲" : "▼"} {formatPercent(Math.abs(value), 1)}
    </span>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-600">{label}</span>
    <span className="font-medium text-slate-900">{value}</span>
  </div>
);

const PaidVsOutstandingBar = ({ paidRevenue, unpaidRevenue, totalRevenue }) => {
  if (!totalRevenue || totalRevenue <= 0) {
    return (
      <div className="text-xs text-slate-500">No revenue yet to compare.</div>
    );
  }

  const paidRatio = paidRevenue / totalRevenue;
  const unpaidRatio = unpaidRevenue / totalRevenue;

  return (
    <div className="space-y-2">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full bg-emerald-500" style={{ flex: paidRatio }} />
        <div className="h-full bg-amber-400" style={{ flex: unpaidRatio }} />
      </div>
      <div className="flex justify-between text-[11px] text-slate-600">
        <span>
          {formatCurrency(paidRevenue)} paid (
          {formatPercent(paidRatio * 100, 1)})
        </span>
        <span className="text-right">
          {formatCurrency(unpaidRevenue)} outstanding (
          {formatPercent(unpaidRatio * 100, 1)})
        </span>
      </div>
    </div>
  );
};

const PatternCard = ({ label, subtitle, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex flex-col items-start rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-left hover:bg-slate-100"
  >
    <div className="text-sm font-semibold text-slate-900">{label}</div>
    {subtitle && (
      <div className="mt-0.5 text-xs text-slate-500">{subtitle}</div>
    )}
    <div className="mt-1 text-xs text-slate-600">
      {count} customer{count === 1 ? "" : "s"}
    </div>
  </button>
);

const ServicePatternModal = ({ mode, items, onClose }) => {
  const titleMap = {
    lapsed: "Customers not repeating services",
    active: "Customers repeating services",
    "one-time": "Customers with one-time large jobs",
  };

  const descMap = {
    lapsed:
      "These customers used to have recurring work but are now overdue based on their usual pattern.",
    active:
      "These customers are still following their usual pattern for recurring services.",
    "one-time":
      "High-value jobs that have only happened once – useful for upsell or check-in.",
  };

  const title = titleMap[mode] || "Service details";
  const description = descMap[mode] || "";

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 px-4">
      <div className="max-h-[80vh] w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            {description && (
              <p className="mt-1 text-xs text-slate-500">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600 hover:bg-slate-200"
          >
            Close
          </button>
        </div>

        <div className="mt-3 space-y-3 overflow-y-auto pr-1">
          {items.length === 0 ? (
            <p className="text-sm text-slate-600">
              Nothing to show here right now.
            </p>
          ) : (
            items.map((item, idx) => (
              <div
                key={`${item.customerName}-${item.serviceName}-${idx}`}
                className="border-b border-slate-100 pb-2 last:border-b-0"
              >
                <div className="font-medium text-slate-900">
                  {item.customerName}
                </div>
                <div className="text-xs text-slate-600">{item.serviceName}</div>

                <div className="mt-1 text-xs text-slate-500">
                  Last service: {item.lastDateLabel}
                </div>

                {mode !== "one-time" &&
                  item.medianIntervalDays != null &&
                  item.daysSinceLast != null && (
                    <div className="text-xs text-slate-500">
                      Typical gap: ~{Math.round(item.medianIntervalDays)} days •
                      Current gap: {item.daysSinceLast} days
                    </div>
                  )}

                {typeof item.totalAmount === "number" &&
                  item.totalAmount > 0 && (
                    <div className="text-xs text-slate-500">
                      Total billed: {formatCurrency(item.totalAmount)}
                    </div>
                  )}

                <div className="mt-0.5 text-[11px] text-slate-400">
                  Occurrences: {item.occurrences}
                </div>

                {item.history && item.history.length > 0 && (
                  <div className="mt-1">
                    <div className="text-[11px] text-slate-400">
                      Service history:
                    </div>
                    {item.history.map((h, i) => (
                      <div key={i} className="text-[11px] text-slate-500">
                        • {h.dateLabel}
                        {h.jobName ? ` – ${h.jobName}` : ""}
                        {h.amount > 0 ? ` – ${formatCurrency(h.amount)}` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricsPage;
